'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import type { MutationStatus } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { parse } from 'csv-parse/sync'
import type z from 'zod'

import Dialog from '@/shared/v2/shared/components/Dialog'
import Button from '@/shared/v2/shared/components/Button'
import LoadingButton from '@/shared/v2/shared/components/LoadingButton'
import Paragraph from '@/shared/v2/shared/components/Paragraph'
import Icon from '@/shared/v2/shared/components/Icon'
import { UploadCloud } from 'lucide-react'
import BulkUploadLoader from './BulkUploadLoader'
import BulkUploadError from './BulkUploadError'
import { downloadCsvTemplateFromSchema } from '../utils/bulkUploadUtils'
import type { BulkUploadError as BulkUploadErrorEnum } from '../models/bulkUploadModels'
import type { BulkUploadState } from '../hooks/useBulkUpload'
import PrivilegesButton from '@/shared/v2/shared/components/PrivilegesButton'
import { useDialogState } from '@/shared/hooks/useDialogState'
import { isEmpty } from 'lodash'

export type BulkUploadDialogRootProps<TSchema extends z.ZodTypeAny> = {
    title: string
    schema: TSchema
    mutationStatus: MutationStatus
    canSubmit: boolean
    onReset: () => void
    onSubmit?: React.FormEventHandler<HTMLFormElement>
    onDownloadSuccessFile: () => void
    templateFileName: string
    submitLabel: string
    children?: React.ReactNode
}

function Root<Schema extends z.ZodTypeAny>({
    title,
    schema,
    mutationStatus,
    canSubmit,
    onReset,
    onSubmit,
    onDownloadSuccessFile,
    templateFileName,
    submitLabel,
    children
}: BulkUploadDialogRootProps<Schema>) {
    const tDialog = useTranslations('root.components.bulkUploadDialog')
    const tForm = useTranslations('root.form.actions')
    const { isOpen, onOpenChange } = useDialogState({
        onClose: onReset
    })

    const isLoading = mutationStatus === 'pending'

    return (
        <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
            <Dialog.Trigger asChild>
                <PrivilegesButton data-testid="__bulk-upload-dialog-trigger-button__" intent="secondary">
                    {tDialog('trigger')}
                </PrivilegesButton>
            </Dialog.Trigger>

            <Dialog.Content title={title} className="max-w-lg">
                <form onSubmit={onSubmit} noValidate>
                    <Dialog.Body>
                        <div className="flex flex-col gap-4 text-left">
                            <div className="flex flex-wrap items-baseline gap-2 text-left">
                                <Dialog.Description asChild>
                                    <Paragraph className="m-0 leading-normal">
                                        {tDialog.rich('description', {
                                            link: (chunks) => (
                                                <Button
                                                    data-testid="__bulk-upload-download-template-button__"
                                                    size="small"
                                                    intent="link"
                                                    onClick={() =>
                                                        downloadCsvTemplateFromSchema(schema, templateFileName)
                                                    }
                                                >
                                                    {chunks}
                                                </Button>
                                            )
                                        })}
                                    </Paragraph>
                                </Dialog.Description>
                            </div>

                            {children}
                        </div>
                    </Dialog.Body>

                    <Dialog.Footer>
                        <Dialog.Close asChild>
                            <Button type="button" data-testid="__bulk-upload-close-button__" intent="secondary">
                                {tForm('close')}
                            </Button>
                        </Dialog.Close>

                        {mutationStatus === 'success' ? (
                            <Button
                                data-testid="__bulk-upload-download-result__"
                                intent="primary"
                                onClick={onDownloadSuccessFile}
                                type="button"
                            >
                                {tDialog('downloadResult')}
                            </Button>
                        ) : (
                            <LoadingButton
                                disabled={!canSubmit}
                                data-testid="__bulk-upload-submit-button__"
                                intent="primary"
                                type="submit"
                                isLoading={isLoading}
                            >
                                {submitLabel}
                            </LoadingButton>
                        )}
                    </Dialog.Footer>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    )
}

type FileProps<TSchema extends z.ZodObject<z.ZodRawShape>> = {
    schema: TSchema
    value: File | null
    onChange: (file: File | null) => void
    isLoading: boolean
    isSuccess: boolean
}

type FileViewState = { kind: 'dropzone' } | { kind: 'file' } | { kind: 'loader'; state: BulkUploadState }

function File<TSchema extends z.ZodObject<z.ZodRawShape>>({
    schema,
    value,
    onChange,
    isLoading,
    isSuccess
}: FileProps<TSchema>) {
    const t = useTranslations('root.components.bulkUploadDialog')

    const [validating, setValidating] = useState(false)
    const [pendingMs, setPendingMs] = useState(0)
    const [error, setError] = useState<BulkUploadErrorEnum | null>(null)

    useEffect(() => {
        if (!isLoading) {
            // eslint-disable-next-line react/set-state-in-effect -- syncs this state after commit, as it always has; setting it during render would change when the sync runs
            setPendingMs(0)
            return
        }

        const start = performance.now()
        const id = window.setInterval(() => {
            setPendingMs(performance.now() - start)
        }, 100)

        return () => window.clearInterval(id)
    }, [isLoading])

    const validateCsvFile = useCallback(
        async (csvFile: File): Promise<BulkUploadErrorEnum | null> => {
            try {
                const csvContent = await csvFile.text()

                let records: Record<string, string>[]
                try {
                    records = parse(csvContent, {
                        columns: true,
                        skip_empty_lines: true,
                        trim: true
                    })
                } catch (err) {
                    return { code: 'PARSING_ERROR', originalError: err }
                }

                if (isEmpty(records)) {
                    return { code: 'EMPTY_FILE' }
                }

                const expectedHeaders = Object.keys(schema.shape)
                const csvHeaders = Object.keys(records[0])
                const missingHeaders = expectedHeaders.filter((h) => !csvHeaders.includes(h))

                if (!isEmpty(missingHeaders)) {
                    return {
                        code: 'MISSING_HEADERS',
                        details: { missingHeaders, expectedHeaders, csvHeaders }
                    }
                }

                return null
            } catch (err) {
                return { code: 'UNKNOWN_ERROR', originalError: err }
            }
        },
        [schema]
    )

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (isEmpty(acceptedFiles)) return

            const csv = acceptedFiles[0]
            setValidating(true)
            setError(null)

            const validationError = await validateCsvFile(csv)

            if (validationError) {
                setError(validationError)
                onChange(null)
            } else {
                onChange(csv)
            }

            setValidating(false)
        },
        [onChange, validateCsvFile]
    )

    const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'text/csv': ['.csv'] },
        multiple: false
    })

    const computeLoaderState = (ms: number): BulkUploadState => {
        if (ms < 3000) return 'working_short'
        if (ms < 6000) return 'working_medium'
        return 'working_long'
    }

    const deriveViewState = (): FileViewState => {
        if (isSuccess) return { kind: 'loader', state: 'success' }
        if (validating) return { kind: 'loader', state: 'working_short' }
        if (isLoading) return { kind: 'loader', state: computeLoaderState(pendingMs) }
        if (value) return { kind: 'file' }
        return { kind: 'dropzone' }
    }

    const view = deriveViewState()

    return (
        <div className="w-full flex flex-col gap-2 ta-left">
            {view.kind === 'dropzone' && (
                <div data-testid="__bulk-upload-dropzone__">
                    <div
                        {...getRootProps()}
                        className={`border border-dashed bg-backgroundSecondary rounded-md p-8 text-center transition-colors w-full cursor-pointer
              ${isDragActive ? 'border-borderPositiveSubdued bg-blue-50' : 'border-borderPrimary hover:border-gray-400'}
            `}
                    >
                        <input data-testid="__bulk-upload-file-input__" {...getInputProps()} />
                        <div className="flex flex-col gap-2 items-center text-center">
                            <Icon icon={UploadCloud} />
                            <Paragraph
                                color="darkGrey"
                                size={4}
                                className="inline-flex justify-center gap-1 leading-none"
                            >
                                {t.rich('dropPromptLink', {
                                    link: (chunks) => (
                                        <Button size="small" intent="link" onClick={open} className="h-auto">
                                            {chunks}
                                        </Button>
                                    )
                                })}
                            </Paragraph>
                        </div>
                    </div>
                </div>
            )}

            {view.kind !== 'dropzone' && (
                <div className="border rounded-md p-3 text-center w-full border-borderPrimary">
                    <div className="flex flex-col items-center gap-4">
                        {view.kind === 'loader' && (
                            <div className="w-full flex justify-center py-4" aria-live="polite" aria-busy>
                                <BulkUploadLoader state={view.state} />
                            </div>
                        )}

                        {view.kind === 'file' && value && (
                            <div className="w-full flex items-center justify-between">
                                <Paragraph
                                    data-testid="__bulk-upload-file-name__"
                                    size={3}
                                    color="darkGrey"
                                    className="break-all max-w-full flex-1 text-left"
                                >
                                    {value.name}
                                </Paragraph>
                                <Button
                                    data-testid="__bulk-upload-delete-button__"
                                    intent="negative"
                                    onClick={() => {
                                        onChange(null)
                                        setError(null)
                                    }}
                                    className="ml-4 shrink-0"
                                >
                                    {t('delete')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {error ? <BulkUploadError error={error} /> : null}
        </div>
    )
}

function Extra({ children }: { children: React.ReactNode }) {
    return <div className="flex flex-col gap-4">{children}</div>
}

export const BulkDialog = {
    Root,
    File,
    Extra
}
